import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useContext,
} from "react";
import PropTypes from "prop-types";
import { AuthContext } from "../../../../context/AuthContext";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { CardList } from "../../../ui/Card";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { fetchWithAuth } from "../../../../utils/api";
import {
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../../utils/swalAlert";
import MobileCapture from "../../../ui/MobileCapture";
import FileUploadSection from "../../../ui/FileUploadSection";
import { motion } from "framer-motion";
import certificateService from "../../../../services/certificateService";

const ViewTest = forwardRef(
  (
    {
      classId,
      className,
      defaultTab = "tests",
      institute: propInstitute,
      onPendingCountsChange,
    },
    ref
  ) => {
    const { user, isAuthenticated, primaryRole } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

    const isHomeworkPath =
      defaultTab === "homeworks" ||
      location.pathname.endsWith("/homeworks") ||
      location.pathname.endsWith("/homework");

    const activeTab = isHomeworkPath ? "homeworks" : defaultTab || "tests";
    const isHomeworkTab = activeTab === "homeworks";

    const checkIsHomeworkTest = useCallback((test) => {
      return Boolean(
        test?.isHomeworkTest === true ||
        test?.isHomeworkTest === "true"
      );
    }, []);

    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [selectedTest, setSelectedTest] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadError, setUploadError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewText, setPreviewText] = useState("");
    const [isPreviewTextOpen, setIsPreviewTextOpen] = useState(false);
    const [capturedFile, setCapturedFile] = useState(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [downloadingSubmissionId, setDownloadingSubmissionId] = useState(null);

    // Certificate Preview & Share Modal States
    const [isCertModalOpen, setIsCertModalOpen] = useState(false);
    const [selectedCert, setSelectedCert] = useState(null);
    const [certImageUrl, setCertImageUrl] = useState("");
    const [isCertLoading, setIsCertLoading] = useState(false);
    const [certDownloadLoading, setCertDownloadLoading] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const [isCertZoomed, setIsCertZoomed] = useState(false);

    const [institute, setInstitute] = useState(
      propInstitute || user?.institute || null
    );
    const instituteId = user?.instituteId || user?.institute?._id;

    useEffect(() => {
      if (propInstitute) {
        setInstitute(propInstitute);
      }
    }, [propInstitute]);

  useEffect(() => {
    let isMounted = true;
    const fetchInstituteData = async () => {
      if (!instituteId) return;
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${instituteId}`,
          { credentials: "include" }
        );
        if (!response || !response.ok) return;
        const data = await response.json();
        if (isMounted && data) {
          setInstitute(data);
        }
      } catch (err) {
        console.error("Failed to fetch institute data in ViewTests:", err);
      }
    };

    if (
      instituteId &&
      (!user?.institute ||
        user?.institute?.standardizedTestTemplateIds === undefined)
    ) {
      fetchInstituteData();
    }
    return () => {
      isMounted = false;
    };
  }, [instituteId, serverUrl, user?.institute]);

  const isUdghosh = Boolean(
      user?.institute?.category === "UDGHOSH" ||
      institute?.category === "UDGHOSH"
  );

  const isEntranceExam = Boolean(
      user?.institute?.category === "ENTRANCE_EXAM" ||
      institute?.category === "ENTRANCE_EXAM"
  );

  const standardizedTestTemplateIds =
    institute?.standardizedTestTemplateIds ||
    user?.institute?.standardizedTestTemplateIds;

  const hasStandardizedTemplates = Boolean(
    standardizedTestTemplateIds &&
      Array.isArray(standardizedTestTemplateIds) &&
      standardizedTestTemplateIds.filter(Boolean).length > 0
  );

  const hasPracticeZone = Boolean(
    isUdghosh || isEntranceExam || hasStandardizedTemplates
  );

  const isLanguageInst = Boolean(
      user?.institute?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
      institute?.category === "LANGUAGE_LEARNING_INSTITUTE"
  );

  const examInstructionsMinutesBefore =
    process.env.REACT_APP_EXAM_INSTRUCTIONS_MINUTES_BEFORE;

  const hasFetched = useRef(false);

  // Expose a function to trigger refresh from parent
  useImperativeHandle(ref, () => ({
    refreshTests: () => {
      fetchTests();
    },
  }));

  const fetchTests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/test/get-all/${classId}`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch tests");
      }
      const data = await response.json();

      const attachSubmissionInfo = async (test) => {
        try {
          const submissionResponse = await fetchWithAuth(
            `${serverUrl}/api/answer-paper/get/test/${test._id}`,
            {
              credentials: "include",
            },
          );
          if (!submissionResponse) return;

          let userSubmission = null;
          if (submissionResponse.ok) {
            const submissionData = await submissionResponse.json();
            // Check if the response is not empty
            userSubmission =
              Object.keys(submissionData).length > 0 ? submissionData : null;
          } else {
            // Nothing
          }

          const testDetailsResponse = await fetchWithAuth(
            `${serverUrl}/api/test/get/${test._id}?onlyInfo=true`,
            {
              credentials: "include",
            },
          );
          if (!testDetailsResponse) return;
          if (!testDetailsResponse.ok) {
            return {
              ...test,
              userSubmission: userSubmission,
              issuedCertificate: test.issuedCertificate || null,
              hasIssuedCertificate:
                test.hasIssuedCertificate || Boolean(test.issuedCertificate),
            };
          }
          const testDetails = await testDetailsResponse.json();
          const detailsData = testDetails?.data || testDetails || {};
          const issuedCertificate =
            detailsData.issuedCertificate ||
            test.issuedCertificate ||
            null;
          const hasIssuedCertificate =
            detailsData.hasIssuedCertificate ||
            test.hasIssuedCertificate ||
            Boolean(issuedCertificate);

          return {
            ...test,
            dueDate: detailsData.dueDate !== undefined ? detailsData.dueDate : test.dueDate,
            userSubmission: userSubmission,
            scorePublishedDate:
              detailsData.scorePublishedDate || testDetails.scorePublishedDate,
            hasIssuedCertificate: hasIssuedCertificate,
            issuedCertificate: issuedCertificate,
          };
        } catch (err) {
          return {
            ...test,
            userSubmission: null,
            issuedCertificate: test.issuedCertificate || null,
            hasIssuedCertificate:
              test.hasIssuedCertificate || Boolean(test.issuedCertificate),
          };
        }
      };

      if (Array.isArray(data)) {
        const testsWithSubmission = await Promise.all(
          data.map((test) => attachSubmissionInfo(test)),
        );
        setTests(testsWithSubmission);
      } else if (data.tests && Array.isArray(data.tests)) {
        const testsWithSubmission = await Promise.all(
          data.tests.map((test) => attachSubmissionInfo(test)),
        );
        setTests(testsWithSubmission);
      } else {
        throw new Error("Invalid data format received from API.");
      }
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [serverUrl, classId]);

  useEffect(() => {
    if (hasFetched.current) return;
    if (classId) {
      fetchTests();
      hasFetched.current = true;
    } else {
      setError("Invalid class ID.");
      setLoading(false);
    }
  }, [classId, fetchTests]);

  useEffect(() => {
    if (!onPendingCountsChange || loading || !Array.isArray(tests)) return;
    const currentDate = new Date();
    const isTestPending = (t) => {
      if (checkIsHomeworkTest(t) && t.status && t.status !== "Ready") return false;
      if (t.userSubmission) return false;
      const dueDate = t.dueDate ? new Date(t.dueDate) : null;
      const isDueDatePassed = dueDate ? currentDate > dueDate : false;
      if (isDueDatePassed) return false;

      if (t.isOnlineExamination) {
        const startDate = t.scheduledDate ? new Date(t.scheduledDate) : null;
        const isBeforeStart = startDate ? currentDate < startDate : false;
        const entryWindowStart =
          startDate && examInstructionsMinutesBefore
            ? new Date(
                startDate.getTime() - examInstructionsMinutesBefore * 60 * 1000
              )
            : null;
        const isWithinEntryWindow = entryWindowStart
          ? currentDate >= entryWindowStart
          : true;
        if (isBeforeStart && !isWithinEntryWindow) return false;
        return true;
      }
      return true;
    };

    const testsCount = tests.filter(
      (t) => !checkIsHomeworkTest(t) && isTestPending(t)
    ).length;
    const homeworksCount = tests.filter(
      (t) => checkIsHomeworkTest(t) && (!t.status || t.status === "Ready") && isTestPending(t)
    ).length;

    onPendingCountsChange({ tests: testsCount, homeworks: homeworksCount });
  }, [tests, loading, checkIsHomeworkTest, onPendingCountsChange, examInstructionsMinutesBefore]);

  const userDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
  }, []);

  const handleOpenUploadModal = (test) => {
    setSelectedTest(test);
    setIsUploadModalOpen(true);
    setUploadFile(null);
    setCapturedFile(null);
    setUploadError("");
  };

  const onCaptureComplete = (capturedFile) => {
    // Call the stored callback from FileUploadSection
    if (window.__fileUploadCallback) {
      window.__fileUploadCallback(capturedFile);
      window.__fileUploadCallback = null;
    }

    // Keep existing logic for backward compatibility
    if (capturedFile) {
      setCapturedFile(capturedFile);
      setUploadFile(null);
      setIsUploadModalOpen(true);
    }
    setIsCapturing(false);
  };

  const handleCloseCapture = () => {
    setIsCapturing(false);
    setIsUploadModalOpen(true); // Reopen modal when closing capture without completing
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    const file = capturedFile || uploadFile;
    if (!file) {
      setUploadError("Please select a file to upload or capture one.");
      return;
    }

    setIsSubmitting(true);
    setUploadError("");

    const formData = new FormData();
    formData.append("testId", selectedTest._id);
    formData.append("studentId", user._id);
    formData.append("file", file);

    try {
      let response;
      if (
        selectedTest.userSubmission &&
        selectedTest.userSubmission.status === "Pending"
      ) {
        response = await fetchWithAuth(
          `${serverUrl}/api/answer-paper/update/${selectedTest.userSubmission._id}`,
          {
            method: "PUT",
            body: formData,
            credentials: "include",
          },
        );
        if (!response) return;
      } else {
        response = await fetchWithAuth(`${serverUrl}/api/answer-paper/add`, {
          method: "POST",
          body: formData,
          credentials: "include",
        });
        if (!response) return;
      }
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload answer paper");
      }
      swalSuccess(
        `${selectedTest && checkIsHomeworkTest(selectedTest)
          ? "Homework"
          : "Answer paper"
        } uploaded successfully!`
      );
      setIsUploadModalOpen(false);
      fetchTests();
    } catch (err) {
      setUploadError(err.message);
      swalError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Certificate Modal Handlers
  const handleOpenCertificatePreview = async (e, _test, cert) => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
    setSelectedCert(cert);
    setIsCertModalOpen(true);
    setCertImageUrl("");
    setIsCertLoading(true);

    try {
      const blob = await certificateService.getCertificateBlob(cert._id);
      if (blob && blob instanceof Blob) {
        const url = URL.createObjectURL(blob);
        setCertImageUrl(url);
      } else {
        throw new Error("Invalid certificate blob data received.");
      }
    } catch (err) {
      console.error("Failed to load certificate preview:", err);
      swalError(
        "Failed to load certificate preview. You can still download it directly.",
      );
    } finally {
      setIsCertLoading(false);
    }
  };

  const handleDownloadCertFile = async () => {
    if (!selectedCert) return;
    try {
      setCertDownloadLoading(true);
      const sanitizedName = (
        selectedCert.studentName ||
        user?.userName ||
        "Student"
      )
        .replace(/[^a-zA-Z0-9\s]/g, "")
        .replace(/\s+/g, "_");
      const certTitle = (
        selectedCert.templateName ||
        selectedCert.templateId?.name ||
        "Certificate"
      )
        .replace(/[^a-zA-Z0-9\s]/g, "")
        .replace(/\s+/g, "_");
      await certificateService.downloadCertificate(
        selectedCert._id,
        `${certTitle}_${sanitizedName}.png`,
      );
      swalSuccess("Certificate downloaded successfully!");
    } catch (err) {
      console.error("Certificate download error:", err);
      swalError(err?.message || "Failed to download certificate.");
    } finally {
      setCertDownloadLoading(false);
    }
  };

  const getVerificationUrl = () => {
    if (!selectedCert) return "";
    return `${window.location.origin}/certificate/verify/${selectedCert.verificationCode}`;
  };

  const handleCopyLink = () => {
    const url = getVerificationUrl();
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const handleShareLinkedInProfile = () => {
    if (!selectedCert) return;
    const certName =
      selectedCert.templateName ||
      selectedCert.templateId?.name ||
      "Certificate of Achievement";
    const orgName = selectedCert.instituteId?.name || "GradeMe AI";
    const issueDate = new Date(selectedCert.issuedAt || Date.now());
    const year = issueDate.getFullYear();
    const month = issueDate.getMonth() + 1;
    const certUrl = getVerificationUrl();
    const certId = selectedCert.verificationCode || "";

    const linkedInAddUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(
      certName,
    )}&organizationName=${encodeURIComponent(
      orgName,
    )}&issueYear=${year}&issueMonth=${month}&certUrl=${encodeURIComponent(
      certUrl,
    )}&certId=${encodeURIComponent(certId)}`;

    window.open(linkedInAddUrl, "_blank", "noopener,noreferrer");
  };

  const handleShareLinkedInFeed = () => {
    const certUrl = getVerificationUrl();
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      certUrl,
    )}`;
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  };

  const handleShareWhatsApp = () => {
    const certUrl = getVerificationUrl();
    const certTitle =
      selectedCert?.templateName ||
      selectedCert?.templateId?.name ||
      "Certificate of Achievement";
    const text = `🎓 Excited to share my official "${certTitle}" on GradeMe AI! \n\nVerify authenticity here: ${certUrl}`;
    const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  const handleShareTwitter = () => {
    const certUrl = getVerificationUrl();
    const certTitle =
      selectedCert?.templateName ||
      selectedCert?.templateId?.name ||
      "Certificate of Achievement";
    const text = `🎓 Proud to have earned my "${certTitle}" on GradeMe AI! Check out my verified credential:`;
    const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      text,
    )}&url=${encodeURIComponent(certUrl)}`;
    window.open(twUrl, "_blank", "noopener,noreferrer");
  };

  const handleShareInstagram = async () => {
    if (!selectedCert) return;
    const certTitle =
      selectedCert.templateName ||
      selectedCert.templateId?.name ||
      "Certificate of Achievement";
    const certUrl = getVerificationUrl();
    const caption = `🎓 Officially earned: "${certTitle}"! Verified on @GradeMeAI: ${certUrl}`;

    try {
      // 1. Use Web Share API with image file if supported (iOS/Android & supported browsers)
      const blob = await certificateService.getCertificateBlob(selectedCert._id);
      if (blob && navigator.canShare) {
        const file = new File(
          [blob],
          `Certificate_${selectedCert.verificationCode || "GradeMe"}.png`,
          { type: "image/png" },
        );
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: certTitle,
            text: caption,
            files: [file],
          });
          return;
        }
      }
    } catch (err) {
      if (err.name === "AbortError") return; // User cancelled native share sheet
      console.warn("Native share fallback to web flow:", err);
    }

    // 2. Fallback for desktop: Copy caption, download certificate, and open Instagram
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(caption);
      }
    } catch (e) {
      // Ignore clipboard error
    }

    await handleDownloadCertFile();
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");

    swalSuccess(
      "Certificate saved & caption copied! Opening Instagram for you to post your certificate.",
    );
  };

  const handleDownloadAnnotatedPdf = async (answerPaperId, testName) => {
    if (!answerPaperId) {
      swalError("Submission not found.");
      return;
    }

    try {
      setDownloadingSubmissionId(answerPaperId);
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/generate-annotation-pdf/${answerPaperId}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response || !response.ok) {
        const errorData = await response?.json().catch(() => ({}));
        throw new Error(
          errorData?.message || "Failed to download annotated PDF."
        );
      }

      const blob = await response.blob();
      const safeTestName = (testName || "Homework")
        .replace(/[/\\?%*:|"<>]/g, "_")
        .trim();
      const filename = `Checked_Homework_${safeTestName}.pdf`;

      const pdfUrl = URL.createObjectURL(blob);

      const pdfWindow = window.open("", "_blank");

      if (pdfWindow) {
        pdfWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>${filename}</title>
            <meta name="viewport" content="width=device-width, initial-scale=1">
            <style>
              body { margin: 0; padding: 0; background-color: #525659; display: flex; flex-direction: column; height: 100vh; font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; overflow: hidden; }
              .header { background-color: #323639; color: #f1f1f1; padding: 12px 24px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 2px 4px rgba(0,0,0,0.2); z-index: 10; height: 32px; flex-shrink: 0; }
              .title { font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 60%; font-weight: 500; }
              .download-btn { background-color: #1a73e8; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 13px; font-weight: 600; transition: background-color 0.2s; display: flex; align-items: center; gap: 8px; }
              .download-btn:hover { background-color: #1557b0; }
              .download-btn svg { width: 16px; height: 16px; fill: currentColor; }
              .content-area { flex: 1; display: flex; flex-direction: column; position: relative; }
              iframe { width: 100%; height: 100%; border: none; }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="title">${filename}</div>
              <button id="custom-download-btn" class="download-btn">
                <svg viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
                Download PDF
              </button>
            </div>
            <div class="content-area">
              <iframe src="${pdfUrl}"></iframe>
            </div>
          </body>
          </html>
        `);
        pdfWindow.document.close();

        // Safely add the click listener to download the PDF with the designated filename
        const downloadBtn = pdfWindow.document.getElementById(
          "custom-download-btn"
        );
        if (downloadBtn) {
          downloadBtn.addEventListener("click", () => {
            const a = document.createElement("a");
            a.href = pdfUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          });
        }
      } else {
        const newWin = window.open(pdfUrl, "_blank");
        if (!newWin) {
          window.location.href = pdfUrl;
        }
      }
    } catch (err) {
      console.error("Error opening annotated PDF:", err);
      swalError(err.message || "Failed to open annotated PDF.");
    } finally {
      setDownloadingSubmissionId(null);
    }
  };

  const displayedTests = useMemo(() => {
    return tests.filter((test) => {
      const isHomework = checkIsHomeworkTest(test);
      if (isHomework && test.status && test.status !== "Ready") {
        return false;
      }
      return isHomeworkTab ? isHomework : !isHomework;
    });
  }, [tests, isHomeworkTab, checkIsHomeworkTest]);

  const cardsData = displayedTests.map((test) => {
    const currentDate = new Date();
    const dueDate = test.dueDate ? new Date(test.dueDate) : null;
    const startDate = test.scheduledDate ? new Date(test.scheduledDate) : null;
    const isDueDatePassed = dueDate ? currentDate > dueDate : false;
    const isBeforeStart = startDate ? currentDate < startDate : false;
    const isHomework = checkIsHomeworkTest(test) || isHomeworkTab;
    // Show "Take Exam" only within examInstructionsMinutesBefore minutes before scheduledDate
    const entryWindowStart =
      startDate && examInstructionsMinutesBefore
        ? new Date(
          startDate.getTime() - examInstructionsMinutesBefore * 60 * 1000,
        )
        : null;
    const isWithinEntryWindow = entryWindowStart
      ? currentDate >= entryWindowStart
      : true;
    const userSubmission = test.userSubmission;
    let submissionStatus = "";
    let submissionStatusStyle = {};
    let uploadButtonText = "";
    let uploadBtnIcon = "upload";
    let showButton = false;
    let buttonOnClick = () => {
      handleOpenUploadModal(test);
    };

    // Server-verified navigation to exam instructions
    const navigateToExamInstructions = async () => {
      if (test.scheduledDate && examInstructionsMinutesBefore) {
        try {
          const res = await fetchWithAuth(
            `${serverUrl}/api/test/verify-exam-instruction-allowed?testScheduledDate=${encodeURIComponent(test.scheduledDate)}&minutesBeforeEntry=${examInstructionsMinutesBefore}`,
            { credentials: "include" },
          );
          if (!res.ok) {
            const errData = await res.json();
            swalError(
              errData.message ||
              "You cannot access exam instructions at this time.",
            );
            return;
          }
        } catch {
          swalError("Unable to verify. Please check your internet connection.");
          return;
        }
      }
      navigate(`/student/exam-instructions/${test._id}`);
    };

    if (!userSubmission) {
      submissionStatus = "Submission Pending";
      submissionStatusStyle = { backgroundColor: "red" };

      if (test.isOnlineExamination) {
        if (isBeforeStart && !isWithinEntryWindow) {
          // Too early — outside the entry window
          uploadButtonText = "Take Exam";
          uploadBtnIcon = "laptop";
          showButton = false;
          submissionStatus = "Not Started";
          submissionStatusStyle = { backgroundColor: "#64748b" };
        } else if (isDueDatePassed) {
          uploadButtonText = "Exam Closed";
          uploadBtnIcon = "lock";
          showButton = false;
          submissionStatus = "Exam Closed";
          submissionStatusStyle = { backgroundColor: "#64748b" };
        } else {
          // Within entry window or exam already started
          uploadButtonText = "Take Exam";
          uploadBtnIcon = "laptop";
          showButton = true;
          buttonOnClick = navigateToExamInstructions;
        }
      } else {
        uploadButtonText = isHomework ? "Homework" : "Answer Paper";
        showButton = !isDueDatePassed;
        if (isDueDatePassed) {
          submissionStatus = isHomework ? "Past Due" : "Submission Closed";
          submissionStatusStyle = { backgroundColor: "#64748b" };
        }
      }
    } else {
      if (userSubmission.status === "Pending") {
        if (test.isOnlineExamination) {
          if (isDueDatePassed) {
            uploadButtonText = "Exam Closed";
            uploadBtnIcon = "lock";
            showButton = false;
            submissionStatus = "Exam Closed";
            submissionStatusStyle = { backgroundColor: "#64748b" };
          } else {
            submissionStatus = "In Progress";
            submissionStatusStyle = { backgroundColor: "var(--color-1)" };
            uploadBtnIcon = "laptop";
            uploadButtonText = "Take Exam";
            showButton = true;
            buttonOnClick = () =>
              navigate(`/student/exam-instructions/${test._id}`);
          }
        } else {
          submissionStatus = "Submitted";
          submissionStatusStyle = { backgroundColor: "var(--color-2)" };
          uploadBtnIcon = "repeat";
          uploadButtonText = "Resubmit";
          showButton = !isDueDatePassed;
        }
      } else if (
        userSubmission.status === "Graded" ||
        userSubmission.status === "Regrade"
      ) {
        submissionStatus =
          userSubmission.status === "Regrade"
            ? "Regrade Requested"
            : isHomework
              ? "Checked"
              : "Graded";
        submissionStatusStyle = { backgroundColor: "green" };
        uploadBtnIcon = isHomework ? "download" : "square-poll-vertical";
        uploadButtonText = isHomework ? "Checked" : "Results";
        showButton = true;
        buttonOnClick = isHomework
          ? () => handleDownloadAnnotatedPdf(userSubmission._id, test.testName)
          : () => {
              navigate(`/student/results/${userSubmission._id}`, {
                state: { isHomeworkTest: isHomework },
              });
            };
      } else if (userSubmission.status === "Requested") {
        submissionStatus = isHomework ? "Checking Requested" : "Grading Requested";
        submissionStatusStyle = { backgroundColor: "grey" };
        showButton = false;
      }
    }

    const canViewResults =
      test.scorePublishedDate && new Date() > new Date(test.scorePublishedDate);

    if (userSubmission?.status === "Graded" && canViewResults) {
      submissionStatus = isHomework ? "Checked" : "Graded";
      submissionStatusStyle = { backgroundColor: "green" };
      uploadBtnIcon = isHomework ? "download" : "square-poll-vertical";
      uploadButtonText = isHomework ? "Checked" : "View Results";
      showButton = true;
      buttonOnClick = isHomework
        ? () => handleDownloadAnnotatedPdf(userSubmission._id, test.testName)
        : () => {
            navigate(`/student/results/${userSubmission._id}`, {
              state: { isHomeworkTest: isHomework },
            });
          };
    } else if (userSubmission?.status === "Graded" && !canViewResults) {
      submissionStatus = "Submitted";
      submissionStatusStyle = { backgroundColor: "orange" };
      showButton = false;
    }

    const handleRubricPreview = async (rubricId) => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/rubric/get/${rubricId}`,
          {
            credentials: "include",
          },
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch rubric");
        const data = await response.json();
        if (data.url) {
          window.open(data.url, "_blank");
        } else if (data.text) {
          setPreviewText(data.text);
          setIsPreviewTextOpen(true);
        } else {
          swalWarning("No content to preview for this rubric.");
        }
      } catch (err) {
        swalError(err.message);
      }
    };

    return {
      id: test._id,
      title: isHomework
        ? test.testName
        : `${test.testName}${test.testType ? ` - ${test.testType}` : ""}`,
      description: (
        <>
          <span className="submission-status" style={submissionStatusStyle}>
            {submissionStatus}
          </span>
          {!isHomework && test.maxScore && <p>Max Score: {test.maxScore}</p>}
          {test?.scheduledDate && (
            <p>Start Date: {userDate(test.scheduledDate)}</p>
          )}
          {test?.dueDate && <p>Due Date: {userDate(test.dueDate)}</p>}
          {test.testPaperURL && (
            <p>
              <a
                href="#!"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (test.testPaperURL && test.testPaperURL !== "") {
                    window.open(test.testPaperURL, "_blank");
                  } else {
                    swalError(`${isHomework ? "Homework" : "Test"} paper file not available.`);
                  }
                }}
                className="btn-test-paper"
              >
                <i className="fa-regular fa-eye"></i> {isHomework ? "Assessment" : "Test Paper"}
              </a>
            </p>
          )}
          {!isHomework && test.rubricId && (
            <p>
              <a
                href="#!"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRubricPreview(test.rubricId);
                }}
                className="btn-rubric-paper"
              >
                <i className="fa-regular fa-eye"></i> Rubric
              </a>
            </p>
          )}
        </>
      ),
      children: (showButton || test.issuedCertificate) && (
        <div
          style={{
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {showButton && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                buttonOnClick();
              }}
              className="btn-upload"
              disabled={downloadingSubmissionId === userSubmission?._id}
              aria-label={
                isHomework && userSubmission?.status === "Graded"
                  ? "Open Checked Homework"
                  : isHomework
                  ? "Upload Homework"
                  : "Upload Answer Paper"
              }
            >
              {downloadingSubmissionId === userSubmission?._id ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i>{" "}
                  {isHomework ? "Opening..." : "Downloading..."}
                </>
              ) : (
                <>
                  <i className={`fa-solid fa-${uploadBtnIcon}`}></i>{" "}
                  {uploadButtonText}
                </>
              )}
            </button>
          )}
          {test.issuedCertificate && (
            <button
              onClick={(e) =>
                handleOpenCertificatePreview(e, test, test.issuedCertificate)
              }
              className="btn-upload"
              style={{
                backgroundColor: "var(--color-4)",
              }}
              aria-label="View & Share Certificate"
              title="View, share, and download your completion certificate"
            >
              <i className="fa-solid fa-award"></i> Certificate
            </button>
          )}
        </div>
      ),
    };
  });

  if (!isAuthenticated || primaryRole !== "student") {
    return <Navigate to="/login" replace />;
  }

  return (
    <div>
      {loading && <Loader />}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && displayedTests.length === 0 && (
        <motion.div
          className="vtes-container"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          {/* Ambient Glows */}
          <div className="vtes-glow-orb vtes-glow-1"></div>
          <div className="vtes-glow-orb vtes-glow-2"></div>

          {/* Main Card */}
          <div className="vtes-card">
            {/* Central Animated Illustration Hub */}
            <div className="vtes-hero-hub">
              {/* Pulsing Background Rings */}
              <div className="vtes-ring vtes-ring-outer"></div>
              <div className="vtes-ring vtes-ring-mid"></div>

              {/* Floating Feature Badges */}
              <motion.div
                className="vtes-floating-chip vtes-chip-left"
                animate={{ y: [0, -8, 0] }}
                transition={{
                  duration: 3.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <i
                  className={`fa-solid ${
                    isHomeworkTab
                      ? "fa-book-open"
                      : hasPracticeZone
                      ? "fa-bullseye"
                      : "fa-graduation-cap"
                  }`}
                ></i>
                <span>
                  {isHomeworkTab
                    ? "Assignments"
                    : hasPracticeZone
                    ? "Smart Quizzes"
                    : "Assessments"}
                </span>
              </motion.div>

              <motion.div
                className="vtes-floating-chip vtes-chip-right"
                animate={{ y: [0, 8, 0] }}
                transition={{
                  duration: 3.6,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.4,
                }}
              >
                <i
                  className={`fa-solid ${
                    isHomeworkTab
                      ? "fa-pen-nib"
                      : hasPracticeZone
                      ? "fa-brain"
                      : "fa-calendar-check"
                  }`}
                ></i>
                <span>
                  {isHomeworkTab
                    ? "Feedback"
                    : hasPracticeZone
                    ? "AI Insights"
                    : "Upcoming"}
                </span>
              </motion.div>

              {/* Center 3D Rocket / Book / Assessment Icon */}
              <div className="vtes-center-icon">
                <i
                  className={`fa-solid ${
                    isHomeworkTab
                      ? "fa-book-bookmark"
                      : hasPracticeZone
                      ? "fa-rocket"
                      : "fa-clipboard-list"
                  } vtes-rocket-icon`}
                ></i>
                <div className="vtes-icon-sparkle-1">
                  <i className="fa-solid fa-star"></i>
                </div>
                <div className="vtes-icon-sparkle-2">
                  <i className="fa-solid fa-star"></i>
                </div>
              </div>
            </div>

            {/* Headline & Description */}
            <div className="vtes-content">
              <h2 className="vtes-title">
                {isHomeworkTab
                  ? "No Homeworks Assigned"
                  : hasPracticeZone
                  ? "Tests Coming Soon!"
                  : "No Tests Assigned Yet"}
              </h2>
              <p className="vtes-subtitle">
                {isHomeworkTab ? (
                  <>
                    There are no homework assignments assigned to you in{" "}
                    <strong className="vtes-highlight-class">
                      {className && className.trim() ? className : "this class"}
                    </strong>{" "}
                    yet.
                    <br />
                    Check back later when your instructor assigns new homework!
                  </>
                ) : hasPracticeZone ? (
                  <>
                    There are no exams assigned to you in{" "}
                    <strong className="vtes-highlight-class">
                      {className && className.trim() ? className : "this class"}
                    </strong>{" "}
                    yet.
                    <br />
                    Stay in peak form — master key topics and test your knowledge
                    with interactive practice tests!
                  </>
                ) : (
                  <>
                    There are no exams assigned to you in{" "}
                    <strong className="vtes-highlight-class">
                      {className && className.trim() ? className : "this class"}
                    </strong>{" "}
                    yet.
                    <br />
                    Tests will appear here once scheduled by your instructor.
                  </>
                )}
              </p>
            </div>

            {/* Primary Action Button */}
            <div className="vtes-actions">
              {isHomeworkTab ? (
                <motion.button
                  className="vtes-primary-btn"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate(`/student/view-class/${classId}`)}
                >
                  <div className="vtes-btn-shimmer"></div>
                  <i className="fa-solid fa-file"></i>
                  <span>View Class Tests</span>
                  <i className="fa-solid fa-arrow-right vtes-btn-arrow"></i>
                </motion.button>
              ) : hasPracticeZone ? (
                <motion.button
                  className="vtes-primary-btn"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate("/student/practice-tests")}
                >
                  <div className="vtes-btn-shimmer"></div>
                  <i className="fa-solid fa-bolt"></i>
                  <span>Go to Practice Zone</span>
                  <i className="fa-solid fa-arrow-right vtes-btn-arrow"></i>
                </motion.button>
              ) : (
                <motion.button
                  className="vtes-primary-btn"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate("/student")}
                >
                  <div className="vtes-btn-shimmer"></div>
                  <i className="fa-solid fa-arrow-left"></i>
                  <span>
                    {isLanguageInst ? "Back to Batches" : "Back to Classes"}
                  </span>
                </motion.button>
              )}
            </div>

            {/* Motivational Pro Tip / Info Note */}
            <div className="vtes-pro-tip">
              <i
                className={`fa-solid ${
                  !isHomeworkTab && !hasPracticeZone
                    ? "fa-circle-info"
                    : "fa-lightbulb"
                }`}
              ></i>
              <span>
                <strong>
                  {!isHomeworkTab && !hasPracticeZone ? "Note:" : "Pro Tip:"}
                </strong>{" "}
                {isHomeworkTab
                  ? "Completing homework regularly reinforces concepts and strengthens retention!"
                  : hasPracticeZone
                  ? "Practicing just 15 minutes a day can boost test scores!"
                  : "Check back regularly or ask your instructor for upcoming test schedules."}
              </span>
            </div>
          </div>
        </motion.div>
      )}
      {!loading && !error && displayedTests.length > 0 && <CardList cards={cardsData} />}

      {/* Capture Full Screen */}
      {isCapturing && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            background: "white",
          }}
        >
          <MobileCapture
            onComplete={onCaptureComplete}
            onClose={handleCloseCapture}
            mode="answer"
            testId={selectedTest?._id}
            studentId={user._id}
            studentName={`${user.firstName} ${user.lastName}`}
          />
        </div>
      )}

      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
      >
        <h2>
          Upload{" "}
          {selectedTest && checkIsHomeworkTest(selectedTest)
            ? "Homework"
            : "Answer Paper"}{" "}
          for
        </h2>
        <form onSubmit={handleUploadSubmit} className="upload-form">
          <div className="form-group">
            <label htmlFor="answerPaper">{selectedTest?.testName}</label>

            <FileUploadSection
              currentFileUrl={null}
              currentFileName={null}
              isSubmitting={isSubmitting}
              accept=".pdf,.png,.jpg,.jpeg"
              onFileChange={(file, isCaptured) => {
                if (isCaptured) {
                  setCapturedFile(file);
                  setUploadFile(null);
                } else {
                  setUploadFile(file);
                  setCapturedFile(null);
                }
                setUploadError("");
              }}
              onCapture={(callback) => {
                setIsCapturing(true);
                setIsUploadModalOpen(false);
                // Store callback for later use
                window.__fileUploadCallback = callback;
              }}
              onRemoveSelectedFile={() => {
                setUploadFile(null);
                setCapturedFile(null);
              }}
              selectedFile={capturedFile || uploadFile}
              isCaptured={capturedFile !== null}
              browseTitle="Choose File"
              browseDescription="Select PDF or image file from device"
              captureTitle="Capture with Camera"
              captureDescription="Take photos and convert to PDF"
            />

            <small className="helper-text">
              Upload your{" "}
              {selectedTest && checkIsHomeworkTest(selectedTest)
                ? "homework"
                : "answer paper"}{" "}
              as PDF, or image file, or capture using camera
            </small>
          </div>
          {uploadError && <div className="error-message">{uploadError}</div>}
          {!isSubmitting ? (
            <button type="submit" className="btn-submit">
              <i className="fa-solid fa-upload"></i> Upload
            </button>
          ) : (
            <Loader style={{ fontSize: "30px", left: "0" }} />
          )}
        </form>
      </Modal>
      {/* Preview Text Modal */}
      <Modal
        isOpen={isPreviewTextOpen}
        onClose={() => setIsPreviewTextOpen(false)}
      >
        <h2>Rubric Text</h2>
        <div className="rubric-text-preview">
          <p>{previewText}</p>
        </div>
      </Modal>

      {/* Certificate Celebration & Social Share Modal */}
      <Modal
        isOpen={isCertModalOpen}
        onClose={() => {
          setIsCertModalOpen(false);
          setSelectedCert(null);
          setIsCertZoomed(false);
          if (certImageUrl) {
            URL.revokeObjectURL(certImageUrl);
            setCertImageUrl("");
          }
        }}
        modalContentClass={"certificate-modal"}
        maxWidth={"50dvw"}
      >
        {/* Certificate Showcase Presentation Frame */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25, duration: 0.35 }}
          className="cert-showcase-frame"
        >
          {/* Official Badge Pill */}
          <div className="cert-ribbon-tag">
            <i
              className="fa-solid fa-shield-halved"
              style={{ color: "var(--color-1, #f09a21)" }}
            />
            AUTHENTIC & VERIFIED
          </div>

          {/* Live Certificate Image or Skeleton */}
          <div
            style={{
              position: "relative",
              width: "100%",
              minHeight: "260px",
              maxHeight: isCertZoomed ? "75vh" : "380px",
              backgroundColor: "#f8fafc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              cursor: certImageUrl ? "zoom-in" : "default",
              transition: "max-height 0.3s ease",
              zIndex: "1",
            }}
            onClick={() => certImageUrl && setIsCertZoomed(!isCertZoomed)}
            title={
              certImageUrl
                ? isCertZoomed
                  ? "Click to reduce size"
                  : "Click to enlarge preview"
                : ""
            }
          >
            {isCertLoading ? (
              <div style={{ textAlign: "center", padding: "2.5rem" }}>
                <Loader style={{ fontSize: "36px", left: "0" }} />
                <p
                  style={{
                    margin: "0.75rem 0 0 0",
                    fontSize: "0.9rem",
                    color: "#64748b",
                    fontWeight: "600",
                  }}
                >
                  ✨ Preparing your verified certificate...
                </p>
              </div>
            ) : certImageUrl ? (
              <img
                src={certImageUrl}
                alt="Certificate of Achievement"
                style={{
                  maxWidth: "100%",
                  maxHeight: isCertZoomed ? "75vh" : "380px",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <i
                  className="fa-solid fa-image"
                  style={{ fontSize: "2.5rem", color: "#cbd5e1" }}
                />
                <p style={{ margin: "0.5rem 0 0 0", color: "#64748b" }}>
                  Unable to load live preview. You can still download the
                  certificate.
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Quick Credential Highlights Bar */}
        {selectedCert && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            style={{
              backgroundColor: "#f8fafc",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              padding: "0.85rem 1.1rem",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "0.75rem",
              marginBottom: "1.25rem",
              fontSize: "0.82rem",
            }}
          >
            <div>
              <span
                style={{
                  color: "#64748b",
                  display: "block",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                }}
              >
                RECIPIENT
              </span>
              <strong style={{ color: "#0f172a", fontSize: "0.9rem" }}>
                {selectedCert.studentName || user?.userName}
              </strong>
            </div>

            <div>
              <span
                style={{
                  color: "#64748b",
                  display: "block",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                }}
              >
                CERTIFICATE
              </span>
              <strong style={{ color: "#0f172a", fontSize: "0.88rem" }}>
                {selectedCert.templateName ||
                  selectedCert.templateId?.name ||
                  "Certificate"}
              </strong>
            </div>

            <div>
              <span
                style={{
                  color: "#64748b",
                  display: "block",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                }}
              >
                CREDENTIAL ID
              </span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginTop: "2px",
                }}
              >
                <code
                  style={{
                    color: "#0f172a",
                    fontWeight: "700",
                    backgroundColor: "#ffffff",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    border: "1px solid #cbd5e1",
                    fontSize: "0.8rem",
                  }}
                >
                  {selectedCert.verificationCode}
                </code>
              </div>
            </div>

            <div>
              <span
                style={{
                  color: "#64748b",
                  display: "block",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                }}
              >
                ISSUED ON
              </span>
              <strong style={{ color: "#0f172a", fontSize: "0.88rem" }}>
                {new Date(
                  selectedCert.issuedAt || Date.now(),
                ).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </strong>
            </div>
          </motion.div>
        )}

        {/* Action Center: Primary Downloads & Social Proof */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {/* Primary Action Buttons */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              alignItems: "center",
              marginBottom: "1.1rem",
            }}
          >
            {/* Download PNG Button */}
            <button
              type="button"
              onClick={handleDownloadCertFile}
              disabled={certDownloadLoading}
              className="cert-btn-primary-cta"
              title="Download the full-resolution certificate image"
            >
              {certDownloadLoading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" /> Downloading...
                </>
              ) : (
                <>
                  <i
                    className="fa-solid fa-download"
                    style={{ fontSize: "1.1rem" }}
                  />
                  Download Certificate
                </>
              )}
            </button>

            {/* 1-Click Add to LinkedIn Profile Button */}
            <button
              type="button"
              onClick={handleShareLinkedInProfile}
              className="cert-btn-linkedin-add"
              title="Directly add this certificate credential to your LinkedIn profile Licenses & Certifications section"
            >
              <i
                className="fa-brands fa-linkedin"
                style={{ fontSize: "1.2rem" }}
              />
              Add to LinkedIn Profile
            </button>
          </div>

          {/* Social Sharing Hub */}
          <div
            style={{
              borderTop: "1px solid #f1f5f9",
              paddingTop: "0.9rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <i
                className="fa-solid fa-share-nodes"
                style={{
                  color: "var(--color-1, #f09a21)",
                  fontSize: "0.9rem",
                }}
              />
              <span
                style={{
                  fontSize: "0.85rem",
                  fontWeight: "700",
                  color: "#334155",
                }}
              >
                Showcase & Share:
              </span>
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {/* LinkedIn Feed Post */}
              <button
                type="button"
                onClick={handleShareLinkedInFeed}
                className="cert-social-share-btn"
                style={{ color: "#0a66c2", borderColor: "#cbd5e1" }}
                title="Share your certificate on LinkedIn feed"
              >
                <i
                  className="fa-brands fa-linkedin"
                  style={{ fontSize: "0.95rem" }}
                />{" "}
                LinkedIn
              </button>

              {/* WhatsApp */}
              <button
                type="button"
                onClick={handleShareWhatsApp}
                className="cert-social-share-btn"
                style={{ color: "#16a34a", borderColor: "#cbd5e1" }}
                title="Share on WhatsApp"
              >
                <i
                  className="fa-brands fa-whatsapp"
                  style={{ fontSize: "1rem" }}
                />{" "}
                WhatsApp
              </button>

              {/* Twitter / X */}
              <button
                type="button"
                onClick={handleShareTwitter}
                className="cert-social-share-btn"
                style={{ color: "#0f172a", borderColor: "#cbd5e1" }}
                title="Tweet your achievement"
              >
                <i
                  className="fa-brands fa-x-twitter"
                  style={{ fontSize: "0.95rem" }}
                />{" "}
                Post
              </button>

              {/* Instagram */}
              <button
                type="button"
                onClick={handleShareInstagram}
                className="cert-social-share-btn"
                style={{ color: "#e1306c", borderColor: "#cbd5e1" }}
                title="Download image and copy caption for Instagram"
              >
                <i
                  className="fa-brands fa-instagram"
                  style={{ fontSize: "1rem" }}
                />{" "}
                Instagram
              </button>

              {/* Copy Verification Link */}
              <button
                type="button"
                onClick={handleCopyLink}
                className="cert-social-share-btn"
                style={{
                  backgroundColor: copiedLink ? "#dcfce7" : "#ffffff",
                  borderColor: copiedLink ? "#86efac" : "#cbd5e1",
                  color: copiedLink ? "#15803d" : "#334155",
                }}
                title="Copy permanent verification link"
              >
                <i
                  className={
                    copiedLink ? "fa-solid fa-check" : "fa-solid fa-link"
                  }
                />
                {copiedLink ? "Copied Link!" : "Copy Link"}
              </button>
            </div>
          </div>
        </motion.div>
      </Modal>
    </div>
  );
});

ViewTest.propTypes = {
  classId: PropTypes.string.isRequired,
  className: PropTypes.string,
  defaultTab: PropTypes.string,
  institute: PropTypes.object,
  onPendingCountsChange: PropTypes.func,
};

export default ViewTest;
